import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import InlineFeedPost from '../components/InlineFeedPost'
import PostModal from '../components/PostModal'
import SkeletonFeed from '../components/SkeletonFeed'
import { useNavigate, Link } from 'react-router-dom'
import PressLeaderboard from '../components/PressLeaderboard'

const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSei7zcQ7kRy7yK-FrBC5EqgaFD1zuYiQJWLsxdwIJFNTnki7w/viewform'
const PAGE_SIZE = 8

function SkeletonPost() {
  return (
    <div style={{ borderBottom: '1px solid var(--rule)', padding: '2rem', animation: 'shimmer 1.6s ease-in-out infinite' }}>
      <div style={{ height: 11, width: '18%', background: 'var(--rule)', marginBottom: '0.9rem' }} />
      <div style={{ height: 28, width: '72%', background: 'var(--rule)', marginBottom: '0.6rem' }} />
      <div style={{ height: 28, width: '52%', background: 'var(--rule)', marginBottom: '1rem' }} />
      <div style={{ height: 14, width: '88%', background: 'var(--rule)', marginBottom: '0.4rem' }} />
      <div style={{ height: 14, width: '65%', background: 'var(--rule)', marginBottom: '1.25rem' }} />
      <div style={{ height: 10, width: '30%', background: 'var(--rule)' }} />
    </div>
  )
}

export default function CulturePage() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [featured, setFeatured] = useState(null)
  const [tags, setTags] = useState([])
  const [activeTag, setActiveTag] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(0)
  const [selectedPostId, setSelectedPostId] = useState(null)
  const sentinelRef = useRef(null)

  async function fetchPosts(pageNum = 0, tag = null, append = false) {
    let q = supabase
      .from('posts')
      .select('*, profiles!posts_author_id_fkey(id, display_name, column_inches, is_voice)')
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1)
    if (tag) q = q.eq('tag', tag)
    const { data, error } = await q

    if (error) {
      console.error('Feed fetch error:', error.message, error.code)
      // Fallback: try without profiles join in case of RLS issue on profiles
      let q2 = supabase
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1)
      if (tag) q2 = q2.eq('tag', tag)
      const { data: data2, error: error2 } = await q2
      if (error2) { console.error('Feed fallback error:', error2.message); setHasMore(false); return }
      processResults(data2 || [], pageNum, tag, append)
      return
    }

    processResults(data || [], pageNum, tag, append)
  }

  async function processResults(results, pageNum, tag, append) {
    if (append) {
      setPosts(prev => [...prev, ...results])
    } else {
      setPosts(results)
      if (pageNum === 0 && !tag) {
        const { data: all } = await supabase
          .from('posts').select('id, tag, featured').eq('status', 'published')
        const allData = all || []
        setTags([...new Set(allData.map(a => a.tag).filter(Boolean))])
        const featId = allData.find(a => a.featured)?.id
        if (featId) {
          const { data: feat } = await supabase
            .from('posts')
            .select('*, profiles!posts_author_id_fkey(id, display_name, column_inches, is_voice)')
            .eq('id', featId).single()
          setFeatured(feat || null)
        } else {
          setFeatured(null)
        }
      }
    }
    setHasMore(results.length === PAGE_SIZE)
  }

  useEffect(() => {
    setLoading(true)
    setPage(0)
    // Small delay on mount lets Supabase restore session from localStorage
    // before the first query fires — prevents empty results on hard refresh
    const delay = posts.length === 0 ? 50 : 0
    const t = setTimeout(() => {
      fetchPosts(0, activeTag, false).finally(() => setLoading(false))
    }, delay)
    return () => clearTimeout(t)
  }, [activeTag])

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return
    const obs = new IntersectionObserver(async ([entry]) => {
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        setLoadingMore(true)
        const nextPage = page + 1
        await fetchPosts(nextPage, activeTag, true)
        setPage(nextPage)
        setLoadingMore(false)
      }
    }, { threshold: 0.1 })
    obs.observe(sentinelRef.current)
    return () => obs.disconnect()
  }, [hasMore, loadingMore, loading, page, activeTag])

  // Search result post open via event
  useEffect(() => {
    const handler = e => setSelectedPostId(e.detail)
    window.addEventListener('openPost', handler)
    return () => window.removeEventListener('openPost', handler)
  }, [])

  function removePost(id) {
    setPosts(prev => prev.filter(p => p.id !== id))
  }

  const feedPosts = featured && !activeTag
    ? posts.filter(p => p.id !== featured.id)
    : posts

  return (
    <div className="section-page">
      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--rule)', background: 'var(--cream)' }}>
        <div className="feed-page-header">
          <p className="section-page-eyebrow">the feed</p>
          <h1 className="feed-page-title">say it.<br />be heard.</h1>
          <p className="feed-page-desc" style={{ marginTop: '0.75rem' }}>
            no algorithm deciding what's worth hearing. opinions, arguments, observations — anything you've actually thought about. the place for perspectives that deserve more than a caption.
          </p>
          <div style={{ marginTop: '1.5rem' }}>
            <Link
              to={user ? '/write' : '/auth'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                fontSize: '0.95rem', letterSpacing: '-0.01em',
                color: 'var(--paper)', background: 'var(--accent)',
                padding: '0.75rem 1.6rem', border: 'none', cursor: 'pointer',
                textDecoration: 'none', transition: 'all 0.2s',
              }}
              className="write-btn-hover"
            >
              ✦ start writing
            </Link>
          </div>

        </div>
      </div>

      <PressLeaderboard />

      {/* Featured */}
      {featured && !activeTag && (
        <div className="feed-featured">
          <div style={{ height: '2rem' }} />
          <div
            className="feed-featured-card"
            onClick={() => setSelectedPostId(featured.id)}
            role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setSelectedPostId(featured.id)}
          >
            {featured.cover_image && (
              <div className="feed-featured-card-image" style={{ backgroundImage: `url(${featured.cover_image})` }} />
            )}
            <div className="feed-featured-inner">
              <p className="feed-featured-eyebrow">Featured piece</p>
              <h2 className="feed-featured-title">{featured.title}</h2>
              {featured.excerpt && <p className="feed-featured-excerpt">{featured.excerpt}</p>}
              <div className="feed-featured-meta">
                <span>{featured.profiles?.display_name}</span>
                {featured.read_time && <span>{featured.read_time} min read</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag filter */}
      {tags.length > 0 && (
        <div className="feed-tag-bar" style={{ paddingTop: featured && !activeTag ? 0 : '1.5rem' }}>
          <button className={`section-tag-btn ${!activeTag ? 'active' : ''}`} onClick={() => setActiveTag(null)}>All</button>
          {tags.map(t => (
            <button key={t} className={`section-tag-btn ${activeTag === t ? 'active' : ''}`}
              onClick={() => setActiveTag(activeTag === t ? null : t)}>{t}</button>
          ))}
        </div>
      )}

      {/* Feed */}
      <div className="feed" style={{ paddingBottom: 0 }}>
        {loading ? (
          <SkeletonFeed count={5} />
        ) : feedPosts.length === 0 ? (
          <div className="empty-state">
            <p>{activeTag ? `No pieces tagged "${activeTag}" yet.` : 'The feed is warming up. Be the first to write something.'}</p>

          </div>
        ) : (
          <>
            {feedPosts.map(post => (
              <InlineFeedPost
                key={post.id}
                post={post}
                onDelete={() => removePost(post.id)}
                onOpen={id => setSelectedPostId(id)}
              />
            ))}
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <SkeletonPost />
                <SkeletonPost />
              </div>
            )}
            {!hasMore && feedPosts.length > 0 && (
              <div style={{ padding: '3rem 2rem', textAlign: 'center', borderTop: '1px solid var(--rule)' }}>
                <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.1em' }}>
                  You've read everything. For now.
                </p>
                <a href={FORM_URL} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ marginTop: '1.25rem', display: 'inline-flex' }}>
                  start writing →
                </a>
              </div>
            )}
          </>
        )}
      </div>

      {/* Post modal — opens for all feed posts and search results */}
      {selectedPostId && (
        <PostModal
          postId={selectedPostId}
          onClose={() => setSelectedPostId(null)}
          onNavigateWriter={id => { setSelectedPostId(null); setTimeout(() => navigate('/writer/' + id), 50) }}
        />
      )}
    </div>
  )
}
